import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { RefreshCw, Database, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

interface BackfillResult {
  campaignId: string;
  campaignName: string;
  status: 'success' | 'error' | 'skipped';
  message: string;
  locationsRestored: number;
  creativesRestored: number;
}

export const CampaignBackfillTool = () => {
  const [isScanning, setIsScanning] = useState(false);
  const [isBackfilling, setIsBackfilling] = useState(false);
  const [results, setResults] = useState<BackfillResult[]>([]);
  const [campaignsNeedingBackfill, setCampaignsNeedingBackfill] = useState<any[]>([]);
  const { toast } = useToast();

  const scanForMissingData = async () => {
    setIsScanning(true);
    setResults([]);
    
    try {
      // Find saved_campaigns with empty locations or creatives
      const { data: savedCampaigns, error: savedError } = await supabase
        .from('saved_campaigns')
        .select('id, campaign_id, campaign_name, selected_locations, creative_urls, creator_email, created_at')
        .order('created_at', { ascending: false });

      if (savedError) throw savedError;

      // Filter campaigns that need backfill
      const needsBackfill = savedCampaigns?.filter(sc => {
        const locationsEmpty = !sc.selected_locations || 
          (Array.isArray(sc.selected_locations) && sc.selected_locations.length === 0);
        const creativesEmpty = !sc.creative_urls || sc.creative_urls.length === 0;
        return locationsEmpty || creativesEmpty;
      }) || [];

      setCampaignsNeedingBackfill(needsBackfill);

      toast({
        title: "Scan Complete",
        description: `Found ${needsBackfill.length} campaigns needing data restoration`,
      });
    } catch (error) {
      console.error('Error scanning campaigns:', error);
      toast({
        title: "Scan Failed",
        description: "Could not scan campaigns for missing data",
        variant: "destructive",
      });
    } finally {
      setIsScanning(false);
    }
  };

  const runBackfill = async () => {
    if (campaignsNeedingBackfill.length === 0) {
      toast({
        title: "No Campaigns to Backfill",
        description: "Please scan for missing data first",
        variant: "destructive",
      });
      return;
    }

    setIsBackfilling(true);
    const backfillResults: BackfillResult[] = [];

    try {
      // Get all campaigns from the main campaigns table as backup source
      const { data: mainCampaigns, error: mainError } = await supabase
        .from('campaigns')
        .select('id, brand_name, selected_locations, creative_urls, email, company_info');

      if (mainError) throw mainError;

      // Create a map for quick lookup
      const campaignMap = new Map(mainCampaigns?.map(c => [c.id, c]) || []);

      for (const savedCampaign of campaignsNeedingBackfill) {
        const result: BackfillResult = {
          campaignId: savedCampaign.campaign_id,
          campaignName: savedCampaign.campaign_name || 'Unknown',
          status: 'skipped',
          message: 'No backup data found',
          locationsRestored: 0,
          creativesRestored: 0,
        };

        try {
          // Try to find matching campaign in main campaigns table
          const mainCampaign = campaignMap.get(savedCampaign.campaign_id);
          
          if (mainCampaign) {
            const updateData: any = {};
            
            // Check if main campaign has locations data
            const savedLocationsEmpty = !savedCampaign.selected_locations || 
              (Array.isArray(savedCampaign.selected_locations) && savedCampaign.selected_locations.length === 0);
            const mainHasLocations = mainCampaign.selected_locations && 
              ((Array.isArray(mainCampaign.selected_locations) && mainCampaign.selected_locations.length > 0) ||
               (typeof mainCampaign.selected_locations === 'object' && Object.keys(mainCampaign.selected_locations).length > 0));

            if (savedLocationsEmpty && mainHasLocations) {
              updateData.selected_locations = mainCampaign.selected_locations;
              result.locationsRestored = Array.isArray(mainCampaign.selected_locations) 
                ? mainCampaign.selected_locations.length 
                : 1;
            }

            // Check if main campaign has creatives data
            const savedCreativesEmpty = !savedCampaign.creative_urls || savedCampaign.creative_urls.length === 0;
            const mainHasCreatives = mainCampaign.creative_urls && mainCampaign.creative_urls.length > 0;

            if (savedCreativesEmpty && mainHasCreatives) {
              updateData.creative_urls = mainCampaign.creative_urls;
              result.creativesRestored = mainCampaign.creative_urls.length;
            }

            // Perform update if we have data to restore
            if (Object.keys(updateData).length > 0) {
              const { error: updateError } = await supabase
                .from('saved_campaigns')
                .update({
                  ...updateData,
                  updated_at: new Date().toISOString(),
                })
                .eq('id', savedCampaign.id);

              if (updateError) {
                result.status = 'error';
                result.message = updateError.message;
              } else {
                result.status = 'success';
                result.message = `Restored ${result.locationsRestored} locations and ${result.creativesRestored} creatives`;
              }
            } else {
              result.status = 'skipped';
              result.message = 'Main campaign also has no data';
            }
          } else {
            result.status = 'skipped';
            result.message = 'No matching campaign found in main table';
          }
        } catch (err: any) {
          result.status = 'error';
          result.message = err.message || 'Unknown error';
        }

        backfillResults.push(result);
      }

      setResults(backfillResults);

      const successCount = backfillResults.filter(r => r.status === 'success').length;
      const errorCount = backfillResults.filter(r => r.status === 'error').length;

      toast({
        title: "Backfill Complete",
        description: `${successCount} restored, ${errorCount} errors, ${backfillResults.length - successCount - errorCount} skipped`,
        variant: successCount > 0 ? "default" : "destructive",
      });

    } catch (error: any) {
      console.error('Error during backfill:', error);
      toast({
        title: "Backfill Failed",
        description: error.message || "An error occurred during backfill",
        variant: "destructive",
      });
    } finally {
      setIsBackfilling(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <RefreshCw className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <Badge className="bg-green-100 text-green-800">Success</Badge>;
      case 'error':
        return <Badge variant="destructive">Error</Badge>;
      default:
        return <Badge variant="secondary">Skipped</Badge>;
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5" />
          Campaign Data Backfill Tool
        </CardTitle>
        <CardDescription>
          Scan and restore missing selected_locations and creative_urls from backup data in the campaigns table
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-4">
          <Button 
            onClick={scanForMissingData} 
            disabled={isScanning || isBackfilling}
            variant="outline"
          >
            {isScanning ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Scanning...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Scan for Missing Data
              </>
            )}
          </Button>

          <Button 
            onClick={runBackfill} 
            disabled={isBackfilling || isScanning || campaignsNeedingBackfill.length === 0}
          >
            {isBackfilling ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Backfilling...
              </>
            ) : (
              <>
                <Database className="mr-2 h-4 w-4" />
                Run Backfill ({campaignsNeedingBackfill.length})
              </>
            )}
          </Button>
        </div>

        {campaignsNeedingBackfill.length > 0 && results.length === 0 && (
          <div className="p-4 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">
              Found <strong>{campaignsNeedingBackfill.length}</strong> campaigns with missing data.
              Click "Run Backfill" to restore data from the main campaigns table.
            </p>
          </div>
        )}

        {results.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium">Backfill Results</h4>
            <ScrollArea className="h-[300px] border rounded-lg p-4">
              <div className="space-y-3">
                {results.map((result, index) => (
                  <div 
                    key={index} 
                    className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg"
                  >
                    {getStatusIcon(result.status)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium truncate">{result.campaignName}</span>
                        {getStatusBadge(result.status)}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{result.message}</p>
                      <p className="text-xs text-muted-foreground mt-1 font-mono">
                        ID: {result.campaignId}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
